import { ref } from 'vue'

import { defineStore } from 'pinia'

/**
 * Push notifications store — holds deferred FCM token state that must survive
 * across the composable's watchers and the plugin lifecycle.
 *
 * - `pendingFcmToken`: a token obtained before `locationId` was ready; registered
 *   once the location watch fires.
 * - `cachedDeviceFcmToken`: the last device token obtained from Firebase, reused
 *   on location change instead of re-prompting the SDK.
 *
 * Moved off composable module-level `let`s so state ownership is explicit and
 * testable (PR #2797 review: usePushNotifications.ts:16).
 */
export const usePushNotificationsStore = defineStore('push-notifications', () => {
  const pendingFcmToken = ref<string | null>(null)
  const cachedDeviceFcmToken = ref<string | null>(null)

  function setPendingFcmToken(token: string | null): void {
    pendingFcmToken.value = token
  }

  function setCachedDeviceFcmToken(token: string | null): void {
    cachedDeviceFcmToken.value = token
  }

  /** Clear deferred registration token state (logout / plugin teardown). */
  function reset(): void {
    pendingFcmToken.value = null
    cachedDeviceFcmToken.value = null
  }

  return {
    pendingFcmToken,
    cachedDeviceFcmToken,
    setPendingFcmToken,
    setCachedDeviceFcmToken,
    reset,
  }
})
